/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { VerifyCodeForm } from '@veeam-vspc/components';

import type { TokenManagerErrorResponse } from '@veeam-vspc/core';
import type { VerifyCodeFormState } from '@veeam-vspc/components';

import { useAppServices } from 'views/providers/AppProvider/hooks';
import { useLang } from 'views/providers/LangProvider/hooks';
import { useLoginStore } from '../../hooks';
import { BaseFactor } from '../BaseFactor';

import type { VerifyCodeFactor } from '../../interfaces';

export interface VerifyCodeProps {
    totalAttempts?: number;
}

export const VerifyCode = observer(({ totalAttempts = 2 }: VerifyCodeProps) => {
    const { tokenManagerService } = useAppServices();
    const lang = useLang();
    const loginStore = useLoginStore();
    const [disabled, setDisabled] = useState(false);
    const factor = loginStore.currentFactor as VerifyCodeFactor;
    const { mfaToken, login } = factor;

    return (
        <BaseFactor>
            <VerifyCodeForm
                title={lang.VERIFICATION}
                onReturnButtonClick={() => loginStore.onFinishFactorHandler({ goToLoginStart: true })}
                error={loginStore.error}
                loading={loginStore.isLoading}
                disabled={disabled}
                onChange={() => loginStore.resetMessages()}
                logIn={({ code }: VerifyCodeFormState) => {
                    loginStore.isLoading = true;

                    tokenManagerService.mfa(mfaToken, code)
                        .then(resp => loginStore.onFinishFactorHandler({ ...resp, login }))
                        .catch(({ errorDescription }: TokenManagerErrorResponse) => {
                            loginStore.isLoading = false;

                            if (factor.attempts >= totalAttempts) {
                                setDisabled(true);
                                loginStore.error = lang.FAILED_TO_CONFIRM_THE_VERIFICATION;
                                return;
                            }

                            factor.attempts += 1;
                            loginStore.error = errorDescription || lang.FAILED_TO_AUTHENTICATE_A_USER_ACCOUNT;
                        });
                }}
                texts={{
                    placeholderMFACode: lang.CODE,
                    actionLogIn: lang.LOG_IN,
                    errorRequired: lang.THE_FIELD_IS_REQUIRED,
                    errorMaxLength: lang.THE_MAXIMUM_LENGTH_IS,
                    warningMFA: lang.YOUR_ACCOUNT_USES_TWO_FACTOR_AUTHENTICATION,
                    returnButtonText: login,
                    matchErrorMessage: lang.VERIFICATION_CODE_FORMAT_INVALID,
                }}
            />
        </BaseFactor>
    );
});
